/**
 * Find in chat (CRM chats.js searchChatMessagesOnServer): OnlyFans' own
 * message search for the fan (`messages/get` with `filter`), kept to messages
 * whose text holds the words, newest hit first; the Transcript shows only the
 * hits while a search is on.
 */
import { useCallback, useRef, useState } from 'react';
import { crmFetch } from './api';
import { parseMessagesPage, type ChatMessage } from './messages';

const SEARCH_LIMIT = 100;

export interface ChatSearch {
  query: string;
  /** Oldest first, as the Transcript draws them; null until a search ran. */
  results: ChatMessage[] | null;
  /** Hits newest first; `index` is the current one. */
  index: number;
  busy: boolean;
  error: string | null;
}

export function useChatSearch(accountId: string, fanId: string, myUsername: string) {
  const [search, setSearch] = useState<ChatSearch>({ query: '', results: null, index: 0, busy: false, error: null });
  const seq = useRef(0);

  const run = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      const n = ++seq.current;
      if (!query) {
        setSearch({ query: '', results: null, index: 0, busy: false, error: null });
        return;
      }
      setSearch((s) => ({ ...s, query, busy: true, error: null }));
      try {
        const body = await crmFetch('/v1/messages/get', { accountId, body: { fan_id: fanId, limit: SEARCH_LIMIT, filter: query } });
        if (n !== seq.current) return;
        const q = query.toLowerCase();
        const results = parseMessagesPage(body, { fanId, myUsername }).messages.filter((m) => m.text.toLowerCase().includes(q));
        setSearch({ query, results, index: 0, busy: false, error: null });
      } catch (err) {
        if (n === seq.current) setSearch({ query, results: [], index: 0, busy: false, error: (err as Error).message });
      }
    },
    [accountId, fanId, myUsername],
  );

  const step = useCallback((by: 1 | -1) => {
    setSearch((s) => {
      const n = s.results?.length ?? 0;
      return n ? { ...s, index: Math.max(0, Math.min(n - 1, s.index + by)) } : s;
    });
  }, []);

  const reset = useCallback(() => {
    seq.current += 1;
    setSearch({ query: '', results: null, index: 0, busy: false, error: null });
  }, []);

  const results = search.results;
  /** The current hit's message id (hits run newest first). */
  const currentId = results && results.length ? results[results.length - 1 - search.index]!.id : null;
  return { search, run, step, reset, currentId };
}
